/**
 * @fileoverview アプリケーションエントリーポイント
 * @description React SPAのルートコンポーネントをマウント
 */
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { QueryClientProvider } from '@tanstack/react-query'

import { queryClient } from '@/lib/query-client'
import { routeTree } from './routeTree.gen'
import '@/index.css'

/**
 * TanStack Router インスタンスを作成
 * @description ファイルベースルーティングで自動生成されたrouteTreeを使用
 */
const router = createRouter({
  routeTree,
  // デフォルトの404表示
  defaultNotFoundComponent: () => (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900">404</h1>
        <p className="mt-2 text-gray-600">ページが見つかりませんでした</p>
        <a href="/" className="mt-4 inline-block text-primary hover:underline">
          ホームに戻る
        </a>
      </div>
    </div>
  ),
  // TanStack Query との連携
  context: {
    queryClient,
  },
})

/**
 * TanStack Router の型定義を登録
 * @description 型安全なナビゲーションのために必要
 */
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

/**
 * ルート要素を取得してReactアプリケーションをマウント
 */
const rootElement = document.getElementById('root')

if (!rootElement) {
  throw new Error('Root element not found')
}

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>
)
